<template>
  <v-row>
    <v-col cols="12" class="pt-2" v-if="userDescription">
      <p class="kbDarkBlueText--text user-description-paragraph">
        {{ userDescription }}
      </p>
    </v-col>

    <v-col cols="12" sm="6" class="pb-0">
      <span class="kbDarkBlueText--text d-block" style="font-size: 12px">{{ $t('general.phone') }}</span>
        <InlineEditor @onSave="updatePerson({ key: 'phone', value: $event })"
                      field-type="phone"
                      :input-rules="[!selectedUser.name && !selectedUser.email ? rules.required : true]"
                      :value="selectedCountryPhone">
          <div class="d-flex align-center" v-if="selectedUser.phone">
            <img style="margin-top: 5px"
                 class="iti-flag mr-1 mt-0"
                 :class="selectedCountryPhone.code">
            <span class="kbDarkBlueText--text text-capitalize">
              +{{ selectedCountryPhone.number }}
            </span>
            <span class="kbDarkBlueText--text text-capitalize">
              {{ selectedCountryPhone.phone }}
            </span>
          </div>

          <div v-else>
            <span class="kbDarkBlueText--text text-capitalize">
              {{ $t('general.enterPhone') }}
            </span>
          </div>
        </InlineEditor>
    </v-col>

    <v-col cols="12" sm="6" class="pb-0">
      <div class="d-flex align-center">
        <span class="kbDarkBlueText--text mr-3"
              style="font-size: 12px">
          {{ $t('general.email') }}
        </span>

        <EmailValidator :email="selectedUser.email"
                        :email-validation-result="selectedUser.emailValidationResult"
                        :email-last-verified-at="selectedUser.emailLastVerifiedAt"
                        @onEmailValidated="onEmailValidated()"/>
      </div>

      <InlineEditor @onSave="updatePerson({ key: 'email', value: $event })"
                    field-type="email"
                    :input-rules="[!selectedUser.name && !selectedUser.phone ? rules.required : true]"
                    :value="selectedUser.email">
        <span class="kbDarkBlueText--text">
          {{ selectedUser.email || 'Ingresa un email' }}
        </span>
      </InlineEditor>
    </v-col>

    <v-col cols="12" sm="6">
      <span class="kbDarkBlueText--text mr-3"
            style="font-size: 12px">
        {{ $t('general.firstName') }}
      </span>
      <InlineEditor @onSave="updatePerson({ key: 'name', value: $event })"
                    :input-rules="[!selectedUser.email && !selectedUser.phone ? rules.required : true]"
                    :value="selectedUser.name">
        <span class="kbDarkBlueText--text">
          {{ selectedUser.name || $t('general.firstName') }}
        </span>
      </InlineEditor>
    </v-col>

    <v-col cols="12" sm="6">
      <span class="kbDarkBlueText--text mr-3"
            style="font-size: 12px">
        {{ $t('general.lastName') }}
      </span>
      <InlineEditor @onSave="updatePerson({ key: 'lastName', value: $event })"
                    :value="selectedUser.lastName">
        <span class="kbDarkBlueText--text">
          {{ selectedUser.lastName || $t('general.lastName') }}
        </span>
      </InlineEditor>
    </v-col>

    <div class="px-3 pb-3">
      <span class="kbDarkBlueText--text"
            style="font-size: 12px">
        {{ $t('general.roles') }}
      </span>

      <InlineEditor field-type="checkbox"
                    :options="filteredRoles"
                    option-value="id"
                    option-label="name"
                    :value="selectedUser.roles.map(item => item.id)"
                    @onSave="updatePerson({ key: 'rolId', value: $event })">
        <template v-if="selectedUser.roles && selectedUser.roles.length > 0">
          <div>
            <v-chip dark
                    v-for="(role, index) in selectedUser.roles"
                    :key="index"
                    class="ma-1"
                    :class="`${mainColor}--text`"
                    :color="mainColor"
                    outlined
                    small
                    pill>
              <v-avatar left
                        class="kbWhite--text"
                        :class="mainColor">{{ index + 1 }}</v-avatar>
              {{ role.name }}
            </v-chip>
          </div>
        </template>

        <template v-else>
          <span>{{ $t('general.addRoles') }}</span>
        </template>
      </InlineEditor>
    </div>

    <v-col cols="12" class="pt-0" v-if="cleanedExtraData.length">
      <v-divider/>
      <ExtraDataFilteringMenu :all-extra-data="allPersonExtraData"
                              :all-extra-data-tags="allPersonExtraDataTags"
                              :person-extra-data-tags-filter-method="personExtraDataTagsFilterMethod"
                              :selected-extra-data="selectedPersonExtraData"
                              :selected-extra-data-tags="selectedPersonExtraDataTags"
                              @onColumnsChange="onColumnsChange" />

        <template v-if="personExtraDataTagsFilterMethod === 'extraData'">
          <v-row>
            <v-col cols="12"
                   class="align-self-stretch"
                   md="6"
                   v-for="(extraData, index) in filteredExtraData"
                   :key="`${extraData.field}-${index}`">
              <span class="kbDarkBlueText--text text-capitalize d-block mb-1"
                    style="font-size: 12px">
                {{ extraData.field }}
              </span>
              <InlineEditor @onSave="updatePerson({ key: 'extraData', value: $event, secondKey: extraData.field })"
                            :field-type="extraData.type || 'text'"
                            :label="extraData.field"
                            :options="extraData.options"
                            option-label="name"
                            option-value="name"
                            :value="extraData.value">
                <span class="kbDarkBlueText--text d-block" style="max-width: 95%; word-wrap: break-word" v-html="$options.filters.extraDataAppearance(extraData)">
                </span>
              </InlineEditor>
            </v-col>
          </v-row>
        </template>

        <template v-else>
          <v-row v-for="(value, key, index) in filteredExtraData" :key="index">
            <v-col cols="12" class="pb-0">
              <span class="kbDarkBlueText--text text-capitalize d-block"
                    style="font-size: 18px; font-weight: 500">
                {{ key }}
              </span>
            </v-col>
            <v-col cols="12"
                   class="align-self-stretch"
                   md="6"
                   v-for="(extraData, idx) in value"
                   :key="`extraData-${idx}`">
              <span class="kbDarkBlueText--text text-capitalize d-block mb-1"
                    style="font-size: 12px">
                {{ extraData.field }}
              </span>
              <InlineEditor @onSave="updatePerson({ key: 'extraData', value: $event, secondKey: extraData.field })"
                            :field-type="extraData.type || 'text'"
                            :label="extraData.field"
                            :options="extraData.options"
                            option-label="name"
                            option-value="name"
                            :value="extraData.value">
                <span class="kbDarkBlueText--text d-block" style="max-width: 95%; word-wrap: break-word">
                  {{ extraData | extraDataAppearance }}
                </span>
              </InlineEditor>
            </v-col>
            <v-col cols="12" class="py-1" v-if="index < Object.keys(filteredExtraData).length - 1">
              <v-divider />
            </v-col>
          </v-row>
        </template>
    </v-col>
  </v-row>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import rules from '@/libs/rules'
// Components
import EmailValidator from '@/components/widgets/EmailValidator'
import ExtraDataFilteringMenu from './ExtraDataFilteringMenu'
// Mixins
import UserKeybeExtraData from '@/mixins/UserKeybeExtraData'
import PeopleMixin from '@/mixins/PeopleMixin'

export default {
  components: { EmailValidator, ExtraDataFilteringMenu },
  mixins: [UserKeybeExtraData, PeopleMixin],
  computed: {
    ...mapGetters(['userExtraDatas', 'appUuid', 'companyUuid']),
    ...mapState({
      roles: state => state.roles.roles,
      selectedUser: state => state.users.selectedUser,
      tags: state => state.apps.tags
    }),
    cleanedExtraData () {
      if (!this.userExtraDatas ||
          !Array.isArray(this.userExtraDatas) ||
          !this.userExtraDatas.length) return []

      return this.userExtraDatas
        .filter(item => {
          return item.field !== 'userDescription' &&
                 item.field !== 'userNotes' &&
                 item.field !== 'userNotesModule'
        })
    },
    filteredExtraData () {
      if (this.personExtraDataTagsFilterMethod === 'extraData') {
        const selectedExtraData = []
        const selectedPersonExtraDataFields = this.selectedPersonExtraData.map(item => item.field)
        for (const field of selectedPersonExtraDataFields) {
          const foundExtraData = this.cleanedExtraData.find(item => item.field === field)
          if (foundExtraData) {
            selectedExtraData.push(foundExtraData)
          }
        }
        return selectedExtraData
      }
      // Initializing groupedExtraDataByTags
      const groupedExtraDataByTags = {}
      const selectedPersonExtraDataTagsValues = []
      for (const tagObj of this.selectedPersonExtraDataTags) {
        selectedPersonExtraDataTagsValues.push(tagObj.value)
        groupedExtraDataByTags[tagObj.value] = []
      }

      for (const data of this.cleanedExtraData) {
        if ((!data.tags || !data.tags.length) && selectedPersonExtraDataTagsValues.includes('noTags')) {
          groupedExtraDataByTags.noTags.push(data)
        } else if (data.tags && data.tags.length) {
          for (const tag of data.tags) {
            if (selectedPersonExtraDataTagsValues.includes(tag)) {
              groupedExtraDataByTags[tag].push(data)
              break
            }
          }
        }
      }
      for (const key in groupedExtraDataByTags) {
        if (!groupedExtraDataByTags[key].length) {
          delete groupedExtraDataByTags[key]
        }
      }
      return groupedExtraDataByTags
    },
    filteredRoles () {
      const foundPromotorInAllRoles = this.roles.find(item => item.name === 'promotor')
      const foundPromotorInUserRoles = this.selectedUser.roles.find(item => item.name === 'promotor')
      if (foundPromotorInAllRoles && foundPromotorInUserRoles) {
        return this.roles.filter(item => item.name !== 'promotor')
      }
      return this.roles
    },
    userDescription () {
      return this.findValueInExtraData('description')
    }
  },
  created () {
    this.setPersonVisibleExtraData()
  },
  data () {
    return {
      allPersonExtraData: [],
      allPersonExtraDataTags: [],
      personExtraDataTagsFilterMethod: 'extraData',
      selectedPersonExtraData: [],
      selectedPersonExtraDataTags: [],
      rules
    }
  },
  methods: {
    findValueInExtraData (field) {
      const { extraData } = this.selectedUser
      let data = ''
      if (extraData && Array.isArray(extraData) && extraData.length > 0) {
        data = extraData.find(item => item.field === field)
        if (data) {
          return data.value
        }
      }
      return data
    },
    onColumnsChange ({ columns, itemsToChange }) {
      const allItemsKey = itemsToChange === 'extraData' ? 'allPersonExtraData' : 'allPersonExtraDataTags'
      const selectedItemsKey = itemsToChange === 'extraData' ? 'selectedPersonExtraData' : 'selectedPersonExtraDataTags'
      // Setting locale columns
      const { allColumns, selectedColumns } = columns
      this[allItemsKey] = allColumns
      this[selectedItemsKey] = selectedColumns
      this.personExtraDataTagsFilterMethod = itemsToChange
      // Updating columns in userKeybeExtraData
      this.updateUserKeybeAppExtraDataItems([
        { key: 'personExtraDataTagsFilterMethod', value: itemsToChange },
        { key: allItemsKey, value: allColumns },
        { key: selectedItemsKey, value: selectedColumns }
      ])
    },
    onEmailValidated () {
      this.$emit('getUser')
    },
    async setPersonVisibleExtraData () {
      await this.$store.dispatch('getAppTagsOfCustomExtraData')
      // Calling the mixin function to init peopleAllAndSelectedHeaders
      const mappedExtraData = this.cleanedExtraData.map(item => ({ field: item.field, value: item.field }))
      const {
        allItems: allPersonExtraData,
        selectedItems: selectedPersonExtraData
      } = this.initUserKeybeAppExtraDataAllAndSelectedItems(
        'allPersonExtraData',
        'selectedPersonExtraData',
        mappedExtraData,
        mappedExtraData,
        'value'
      )
      // Setting personAllAndSelectedExtraData
      this.allPersonExtraData = allPersonExtraData
      this.selectedPersonExtraData = selectedPersonExtraData

      let mappedTags = [{ text: this.$t('general.noTags'), value: 'noTags', translation: 'general.noTags' }]
      if (this.tags && Array.isArray(this.tags)) {
        mappedTags = [...this.tags.map(item => ({ text: item, value: item })), ...mappedTags]
      }

      const {
        allItems: allPersonExtraDataTags,
        selectedItems: selectedPersonExtraDataTags
      } = this.initUserKeybeAppExtraDataAllAndSelectedItems(
        'allPersonExtraDataTags',
        'selectedPersonExtraDataTags',
        mappedTags,
        mappedTags,
        'value'
      )

      // Initializing extraDataTags filterBy method
      this.personExtraDataTagsFilterMethod = this.initUserKeybeAppExtraDataItem(
        'personExtraDataTagsFilterMethod',
        'tags'
      )

      // Setting personAllAndSelectedExtraDataTags
      this.allPersonExtraDataTags = allPersonExtraDataTags
      this.selectedPersonExtraDataTags = selectedPersonExtraDataTags
    },
    setExtraDataTags (selectedTags) {
      this.selectedTags = selectedTags
    },
    updatePerson (data) {
      this.$emit('updatePerson', data)
    }
  },
  props: {
    mainColor: {
      type: String
    },
    selectedCountryPhone: {
      type: Object
    }
  }
}
</script>

<style scoped>
  .user-description-paragraph {
    display: -webkit-box;
    overflow : hidden;
    text-overflow: ellipsis;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
  }
</style>
